import Box, { type BoxProps } from '@mui/material/Box';
import type { ReactNode } from 'react';

import { cssVar } from '../../theme/tokens';
import { mergeSx } from './sizes';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

export interface BadgeProps extends Omit<BoxProps<'span'>, 'children'> {
  tone?: BadgeTone;
  size?: 'sm' | 'md';
  /** Small icon before the text. */
  icon?: ReactNode;
  /** A dot in the text colour before the text (status pills: Published, Draft, Live). */
  dot?: boolean;
  children: ReactNode;
}

const TONES: Record<BadgeTone, { bg: string; fg: string }> = {
  neutral: { bg: cssVar('track'), fg: cssVar('text-muted') },
  primary: { bg: cssVar('primary-subtle'), fg: cssVar('primary-text') },
  success: { bg: cssVar('success-subtle'), fg: cssVar('success-text') },
  warning: { bg: cssVar('warning-subtle'), fg: cssVar('warning-text') },
  danger: { bg: cssVar('danger-subtle'), fg: cssVar('danger-text') },
  info: { bg: cssVar('info-subtle'), fg: cssVar('info-text') },
};

/**
 * A short status label (Published, Draft, Failed) as a pill: tinted background and readable text
 * in both schemes (the v3 pill tones). Not MUI's Badge (the dot or count on an icon).
 */
export function Badge({
  tone = 'neutral',
  size = 'md',
  icon,
  dot = false,
  children,
  sx,
  ...props
}: BadgeProps) {
  const colours = TONES[tone];
  return (
    <Box
      component="span"
      sx={mergeSx(
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          px: size === 'sm' ? 2 : 2.5,
          py: size === 'sm' ? 0.25 : 1,
          borderRadius: cssVar('radius-pill'),
          bgcolor: colours.bg,
          color: colours.fg,
          typography: 'caption',
          fontWeight: 700,
          lineHeight: cssVar('line-height-caption'),
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
          '& .MuiSvgIcon-root': { fontSize: cssVar('font-size-body') },
        },
        sx,
      )}
      {...props}
    >
      {dot ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            // 6 px: padding only, no content.
            p: 0.75,
            borderRadius: cssVar('radius-pill'),
            bgcolor: 'currentColor',
            flex: 'none',
          }}
        />
      ) : null}
      {icon}
      {children}
    </Box>
  );
}
